import React, { useState } from 'react';
import { Theme, TextField, Typography, tooltipClasses, Tooltip, TooltipProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveIcon from '@mui/icons-material/Remove';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import { Courtage, Fund } from '../api/useFunds';
import { toEuroWholeNumber, toPercent, toPercentWholeNumber } from '../utils/numberFormat';
import { bicToName } from '../utils/bicMappings';

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'space-between',
  [theme.breakpoints.only('xs')]: {
    display: 'block',
  },
}));

const HeadingContainer = styled('div')({
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'space-between',
});

const FundNotAvailableWarning = styled(Typography)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));

const Heading = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const Centered = styled('div')(({ theme }) => ({
  textAlign: 'center',
  [theme.breakpoints.only('xs')]: {
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
}));

const CenteredMobileOnly = styled('div')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.only('xs')]: {
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginLeft: theme.spacing(2),
  },
}));

const CenteredDesktopOnly = styled('div')(({ theme }) => ({
  textAlign: 'center',
  [theme.breakpoints.only('xs')]: {
    display: 'none',
  },
}));

const SpacerRightXs = styled('div')(({ theme }) => ({
  [theme.breakpoints.only('xs')]: {
    marginRight: theme.spacing(1),
  },
}));

const LabelText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const SubLabelText = styled(Typography)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  color: theme.palette.text.secondary,
}));

const YesIcon = styled(CheckCircleIcon)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.success.main,
}));

const NoIcon = styled(RemoveIcon)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.error.main,
}));

const WarnIcon = styled(ErrorIcon)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.warning.main,
}));

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => {
  return {
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
  };
});

interface VlIconProps {
  courtage?: Courtage;
  testId: string;
}

const VlIcon = ({ courtage, testId }: VlIconProps) => {
  const { t } = useTranslation();
  return (
    <>
      <LabelText>{t('cashbackCalculator.detailed.fundInput.vl')}</LabelText>
      {courtage?.hasVl ? (
        <YesIcon data-testid={`has-${testId}`} />
      ) : (
        <NoIcon data-testid={`no-${testId}`} />
      )}
    </>
  );
};

interface Props {
  fund: Fund;
  investedAmount: number;
  bic: Bic | 'OTHER_BANK';
  testId: string;
  isCollapsed: boolean;
  onChange: (fund: Fund, amount: number) => void;
  onRemove: (fund: Fund) => void;
}

export const FundInput = ({
  fund,
  investedAmount,
  bic,
  testId,
  isCollapsed,
  onChange,
  onRemove,
}: Props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const compareBic = bic === 'OTHER_BANK' ? 'COBADEHDXXX' : bic;

  const courtage = fund.courtages?.find((c) => {
    return c.bic === compareBic;
  });

  const agio = courtage && investedAmount ? (courtage.agio || 0) * investedAmount : 0;

  const portfolioCommission =
    courtage && investedAmount ? (courtage.portfolioCommission || 0) * investedAmount : 0;

  const handleAmountChange = (event: any) => {
    try {
      const amount = parseInt(event.target.value, 10);
      onChange(fund, amount);
    } catch (err) {
      onChange(fund, 0);
    }
  };

  const handleOpenOrClose = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div data-testid={testId}>
      <HeadingContainer>
        <Heading variant="body1" onClick={() => handleOpenOrClose()}>
          <strong>{fund.name}</strong> ({fund.isin})
          {isCollapsed && !isOpen && investedAmount && (
            <span>; {toEuroWholeNumber(investedAmount)}</span>
          )}
        </Heading>
        <div>
          <CloseIcon
            data-testid={`remove-fund-${fund.isin}`}
            color="primary"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              onRemove(fund);
            }}
          />
        </div>
      </HeadingContainer>
      {!courtage && (
        <FundNotAvailableWarning data-testid="fund-not-available" variant="body2">
          <WarnIcon style={{ fontSize: '20px' }} data-testid="fund-not-available-warning" />
          {t('cashbackCalculator.detailed.fundInput.notAvailableAt', {
            bank: bicToName(bic),
          })}
        </FundNotAvailableWarning>
      )}
      {(!isCollapsed || isOpen) && (
        <Container>
          <div>
            <TextField
              id={`fund-amount-${fund.isin}`}
              inputProps={{
                'data-testid': 'fund-amount-input',
                'data-hj-whitelist': true,
              }}
              label={t('cashbackCalculator.detailed.fundInput.amount')}
              variant="filled"
              type="number"
              defaultValue={investedAmount ? investedAmount?.toString() : undefined}
              onChange={handleAmountChange}
            />
          </div>
          <Centered>
            <SpacerRightXs>
              <LabelText data-testid="agio-in-percent">
                {t('cashbackCalculator.detailed.fundInput.agio')} (
                {toPercent(courtage?.agio ? courtage.agio : 0, 0, 1)})
              </LabelText>
            </SpacerRightXs>
            <SpacerRightXs>
              <Typography variant="h4" component="span" data-testid="agio-amount">
                {toEuroWholeNumber(agio)}
              </Typography>
            </SpacerRightXs>
            {!!courtage?.agio && (
              <SubLabelText variant="body2">
                {courtage?.discountOnAgio === 1 ? (
                  <YesIcon style={{ fontSize: '20px' }} data-testid="full-agio" />
                ) : (
                  <LightTooltip
                    placement="top"
                    title={t('cashbackCalculator.detailed.fundInput.limitedAgioDiscount') || ''}
                  >
                    <WarnIcon style={{ fontSize: '20px' }} data-testid="agio-warning" />
                  </LightTooltip>
                )}
                &nbsp;
                {t('cashbackCalculator.detailed.fundInput.agioDiscount', {
                  percent: toPercentWholeNumber(courtage?.discountOnAgio || 0),
                })}
              </SubLabelText>
            )}
          </Centered>
          <Centered>
            <SpacerRightXs>
              <LabelText data-testid="portfolio-commission-in-percent">
                {t('cashbackCalculator.detailed.fundInput.portfolioCommission')} (
                {toPercent(courtage?.portfolioCommission ? courtage.portfolioCommission : 0)})
              </LabelText>
            </SpacerRightXs>
            <Typography variant="h4" component="span" data-testid="portfolio-commission-amount">
              {toEuroWholeNumber(portfolioCommission)}
            </Typography>
          </Centered>

          <Centered>
            {courtage?.hasSavingsPlan && courtage?.savingsPlanMinAmount && (
              <LightTooltip
                placement="top"
                title={
                  t('cashbackCalculator.detailed.fundInput.savingsPlanTooltip', {
                    from: toEuroWholeNumber(courtage.savingsPlanMinAmount),
                    bank: bicToName(bic),
                  }) || ''
                }
              >
                <LabelText style={{ cursor: 'pointer' }}>
                  {t('cashbackCalculator.detailed.fundInput.savingsPlan')}
                </LabelText>
              </LightTooltip>
            )}
            {!(courtage?.hasSavingsPlan && courtage?.savingsPlanMinAmount) && (
              <LabelText>{t('cashbackCalculator.detailed.fundInput.savingsPlan')}</LabelText>
            )}
            {courtage?.hasSavingsPlan ? (
              <YesIcon data-testid="has-sp" />
            ) : (
              <NoIcon data-testid="no-sp" />
            )}

            <CenteredMobileOnly>
              <VlIcon courtage={courtage} testId="vl-mobile" />
            </CenteredMobileOnly>
          </Centered>
          <CenteredDesktopOnly>
            <VlIcon courtage={courtage} testId="vl" />
          </CenteredDesktopOnly>
          <CenteredDesktopOnly>
            <div></div>
          </CenteredDesktopOnly>
        </Container>
      )}
    </div>
  );
};
